import React, { useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import io from "socket.io-client";
import {
  Badge,
  IconButton,
  TextField,
  Typography,
  Button,
  Box,
  Grid,
  Drawer,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { Userprofile } from "../../redux/actions/auth";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CloseIcon from "@mui/icons-material/Close";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import ChatIcon from "@mui/icons-material/Chat";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import { hideSidebar, showSidebar } from "../../redux/actions/sidebar";
import { hideHeader, showHeader } from "../../redux/actions/header";
import { useParams, useNavigate } from "react-router-dom";

const theme = createTheme({
  palette: {
    mode: "dark",
    text: { primary: "#fff", secondary: "rgba(255,255,255,0.7)" },
    background: { default: "#121212", paper: "#1e1e1e" },
  },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: "none" } } },
    MuiTypography: { styleOverrides: { root: { color: "#fff" } } },
  },
});

const ParticipantVideo = React.forwardRef(({ participant }, ref) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && participant.stream) {
      videoRef.current.srcObject = participant.stream;
      videoRef.current
        .play()
        .catch((e) =>
          console.error(`Error playing video for ${participant.socketId}:`, e)
        );
    }
  }, [participant.stream, participant.socketId]);

  return (
    <video
      ref={(el) => {
        videoRef.current = el;
        if (ref) ref.current = el;
      }}
      autoPlay
      playsInline
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
});

const BASE_URL =
  localStorage.getItem("REACT_APP_WS_URL") || process.env.REACT_APP_WS_URL;
const orgName = localStorage.getItem("org_name");

const polite = true;

const peerConfigConnections = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

export const VideoCall = ({
  Userprofile,
  currentUserDetails,
  hideSidebar,
  showSidebar,
  hideHeader,
  showHeader,
}) => {
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const { roomId } = useParams();
  const navigate = useNavigate();
  const socketRef = useRef();
  const socketIdRef = useRef();
  const localVideoref = useRef();
  const videoRefs = useRef({});
  const audioAnalyzers = useRef({});
  const previewVideoref = useRef();
  const connectionsRef = useRef({});
  const localStreamRef = useRef(null);
  const isMountedRef = useRef(true);
  const recognitionRef = useRef(null);

  const [transcriptionMessages, setTranscriptionMessages] = useState([]);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [videoAvailable, setVideoAvailable] = useState(true);
  const [audioAvailable, setAudioAvailable] = useState(true);
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [video, setVideo] = useState(true);
  const [audio, setAudio] = useState(true);
  const [screen, setScreen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showChatDrawer, setShowChatDrawer] = useState(false);
  const [showAIDrawer, setShowAIDrawer] = useState(false);
  const [screenAvailable, setScreenAvailable] = useState(false);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [newMessages, setNewMessages] = useState(0);
  const [askForUsername, setAskForUsername] = useState(true);
  const [videos, setVideos] = useState([]);
  const [callTime, setCallTime] = useState(0);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [audioDevice, setAudioDevice] = useState("none");
  const [videoDevice, setVideoDevice] = useState("none");
  const [volume, setVolume] = useState(50);
  const [previewStream, setPreviewStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [localVideoReady, setLocalVideoReady] = useState(false);

  const baaliAgent = {
    socketId: "baali-ai-agent",
    stream: null,
    autoplay: true,
    playsinline: true,
    name: "Baali AI Interview Agent",
  };

  const allParticipants = [baaliAgent, ...videos];

  useEffect(() => {
    const loadDeviceLists = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setAudioDevices(devices.filter((d) => d.kind === "audioinput"));
        setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
      } catch (error) {
        console.error("Error enumerating devices:", error);
      }
    };
    loadDeviceLists();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const checkPermissions = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        setVideoAvailable(true);
        stream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        setVideoAvailable(false);
        setCameraError("Camera access denied");
      }
    };
    checkPermissions();
    return () => {
      isMountedRef.current = false;
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
      if (previewStream) {
        previewStream.getTracks().forEach((track) => track.stop());
        setPreviewStream(null);
      }
      Object.values(audioAnalyzers.current).forEach((analyzer) => {
        analyzer.source?.disconnect();
        analyzer.analyser?.disconnect();
        analyzer.audioContext?.close();
      });
      Object.values(connectionsRef.current).forEach((conn) => conn.close());
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, [previewStream]);

  useEffect(() => {
    const toggleCameraPreview = async () => {
      if (!isMountedRef.current) return;
      if (cameraOn && videoAvailable && videoDevice !== "none") {
        try {
          const constraints = { video: { deviceId: { exact: videoDevice } } };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (isMountedRef.current && previewVideoref.current) {
            setPreviewStream(stream);
            previewVideoref.current.srcObject = stream;
            previewVideoref.current
              .play()
              .catch((e) => console.log("Play error:", e));
            setCameraError(null);
          }
        } catch (error) {
          if (isMountedRef.current) {
            console.error("Camera error:", error);
            setCameraOn(false);
            setCameraError("Unable to access camera");
          }
        }
      } else {
        if (previewStream) {
          previewStream.getTracks().forEach((track) => track.stop());
          setPreviewStream(null);
        }
        if (previewVideoref.current) previewVideoref.current.srcObject = null;
        setCameraError(null);
      }
    };
    toggleCameraPreview();
    return () => {
      if (previewStream)
        previewStream.getTracks().forEach((track) => track.stop());
    };
  }, [cameraOn, videoAvailable, videoDevice]);

  useEffect(() => {
    if (!askForUsername) {
      connectToSocketServer();
    }
  }, [askForUsername]);

  useEffect(() => {
    hideSidebar();
    hideHeader();
  }, [hideSidebar, hideHeader]);

  useEffect(() => {
    if (!askForUsername) {
      const timer = setInterval(() => setCallTime((prev) => prev + 1), 1000);
      return () => clearInterval(timer);
    }
  }, [askForUsername]);

  useEffect(() => {
    if (localStreamRef.current && localVideoref.current && !askForUsername) {
      localVideoref.current.srcObject = localStreamRef.current;
      localVideoref.current
        .play()
        .catch((e) => console.error("Local video play error:", e));
      setLocalVideoReady(true);
    }
  }, [localStreamRef.current, askForUsername]);

  const updateVideos = useCallback((socketId, stream) => {
    setVideos((prev) => {
      const exists = prev.find((v) => v.socketId === socketId);
      if (exists && exists.stream === stream) return prev;
      const updatedVideos = exists
        ? prev.map((v) => (v.socketId === socketId ? { ...v, stream } : v))
        : [
            ...prev,
            {
              socketId,
              stream,
              autoplay: true,
              playsinline: true,
              name: socketId,
            },
          ];
      return updatedVideos;
    });
  }, []);

  useEffect(() => {
    if (!askForUsername && audio) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition && !recognitionRef.current) {
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = "en-US";
        recognition.onresult = (event) => {
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              const transcript = result[0].transcript;
              if (socketRef.current) {
                socketRef.current.emit(
                  "transcription",
                  userInfo?.u_name,
                  transcript
                );
              }
              setTranscriptionMessages((prev) => [
                ...prev,
                { sender: userInfo?.u_name, data: transcript },
              ]);
            }
          }
        };
        recognition.onerror = (event) =>
          console.error("Speech recognition error:", event.error);
        recognition.onend = () => {
          if (audio && isMountedRef.current) recognition.start();
        };
        recognition.start();
      }
    } else if (recognitionRef.current && !audio) {
      recognitionRef.current.stop();
    }
  }, [askForUsername, audio, userInfo?.u_name]);

  const getUserMediaSuccess = (stream) => {
    if (localStreamRef.current)
      localStreamRef.current.getTracks().forEach((track) => track.stop());
    localStreamRef.current = stream;
    if (localVideoref.current) {
      localVideoref.current.srcObject = stream;
      localVideoref.current
        .play()
        .catch((e) => console.log("Play error in getUserMediaSuccess:", e));
      setLocalVideoReady(true);
    }
    Object.keys(connectionsRef.current).forEach((id) => {
      if (id !== socketIdRef.current) {
        const connection = connectionsRef.current[id];
        stream.getTracks().forEach((track) => {
          if (
            !connection.getSenders().find((sender) => sender.track === track)
          ) {
            connection.addTrack(track, stream);
          }
        });
        if (connection.signalingState === "stable" && !connection.makingOffer) {
          connection.makingOffer = true;
          connection
            .createOffer()
            .then((description) => connection.setLocalDescription(description))
            .then(() => {
              socketRef.current.emit(
                "signal",
                id,
                JSON.stringify({ sdp: connection.localDescription })
              );
            })
            .catch((e) => console.log("Error creating offer:", e))
            .finally(() => (connection.makingOffer = false));
        }
      }
    });
    stream.getTracks().forEach((track) => {
      track.onended = () => getUserMedia();
    });
  };

  const getDisplayMediaSuccess = (stream) => {
    if (localStreamRef.current)
      localStreamRef.current.getTracks().forEach((track) => track.stop());
    localStreamRef.current = stream;
    if (localVideoref.current) {
      localVideoref.current.srcObject = stream;
      localVideoref.current
        .play()
        .catch((e) => console.log("Play error in getDisplayMediaSuccess:", e));
    }
    Object.keys(connectionsRef.current).forEach((id) => {
      if (id !== socketIdRef.current) {
        const connection = connectionsRef.current[id];
        stream.getTracks().forEach((track) => {
          if (
            !connection.getSenders().find((sender) => sender.track === track)
          ) {
            connection.addTrack(track, stream);
          }
        });
        if (connection.signalingState === "stable" && !connection.makingOffer) {
          connection.makingOffer = true;
          connection
            .createOffer()
            .then((description) => connection.setLocalDescription(description))
            .then(() => {
              socketRef.current.emit(
                "signal",
                id,
                JSON.stringify({ sdp: connection.localDescription })
              );
            })
            .catch((e) =>
              console.log("Error creating offer in getDisplayMedia:", e)
            )
            .finally(() => (connection.makingOffer = false));
        }
      }
    });
    stream.getTracks().forEach((track) => {
      track.onended = () => {
        setScreen(false);
        try {
          if (localVideoref.current && localVideoref.current.srcObject) {
            localVideoref.current.srcObject
              .getTracks()
              .forEach((track) => track.stop());
          }
        } catch (e) {
          console.log("Error stopping tracks on display media end:", e);
        }
        getUserMedia();
      };
    });
  };

  const renegotiateConnection = (socketId, connection) => {
    if (
      !connection ||
      !localStreamRef.current ||
      connection.signalingState !== "stable"
    )
      return;
    const hasTracks = connection.getSenders().length > 0;
    if (!hasTracks) {
      console.log(`No tracks for ${socketId}, renegotiating...`);
      localStreamRef.current.getTracks().forEach((track) => {
        if (!connection.getSenders().find((sender) => sender.track === track)) {
          connection.addTrack(track, localStreamRef.current);
        }
      });
      connection.makingOffer = true;
      connection
        .createOffer()
        .then((description) => connection.setLocalDescription(description))
        .then(() => {
          socketRef.current.emit(
            "signal",
            socketId,
            JSON.stringify({ sdp: connection.localDescription })
          );
        })
        .catch((e) => console.error("Error renegotiating:", e))
        .finally(() => (connection.makingOffer = false));
    }
  };

  const connectToSocketServer = () => {
    socketRef.current = io.connect(BASE_URL, {
      path: `/${orgName}/socket.io`,
      secure: false,
    });
    socketRef.current.on("signal", gotMessageFromServer);
    socketRef.current.on("connect", () => {
      socketRef.current.emit(
        "join-room",
        roomId,
        userInfo.u_id,
        userInfo.u_email
      );
      socketIdRef.current = socketRef.current.id;
      socketRef.current.on("chat-message", (sender, message) => {
        if (sender !== userInfo?.u_name) {
          setMessages((prev) => [...prev, { sender, data: message }]);
        }
      });
      socketRef.current.on("ai-response", (response) => {
        setAiResponse(response);
        setAiChatMessages((prev) => [
          ...prev,
          { sender: "AI", data: response },
        ]);
      });
      socketRef.current.on("user-left", (id) => {
        setVideos((prevVideos) =>
          prevVideos.filter((video) => video.socketId !== id)
        );
        if (videoRefs.current[id] && videoRefs.current[id].srcObject) {
          videoRefs.current[id].srcObject = null;
        }
        if (connectionsRef.current[id]) {
          connectionsRef.current[id].close();
          delete connectionsRef.current[id];
        }
      });
      socketRef.current.on("participants-updated", ({ participantsList }) => {
        const filterList = participantsList.filter(
          (p) => p.email !== userInfo.u_email
        );
        setVideos((prev) =>
          filterList.map((p) => {
            const existing = prev.find((v) => v.socketId === p.socket_id);
            return {
              socketId: p.socket_id || null,
              stream: existing?.stream || null,
              autoplay: true,
              playsinline: true,
              name: p.name,
              email: p.email,
              role: p.role,
            };
          })
        );
      });
      socketRef.current.on("user-joined", (id, clients) => {
        clients.forEach((socketListId) => {
          if (socketListId === socketIdRef.current) return;
          if (!connectionsRef.current[socketListId]) {
            const connection = new RTCPeerConnection(peerConfigConnections);
            connection.makingOffer = false;
            connection.ignoreOffer = false;
            connection.pendingCandidates = [];
            connectionsRef.current[socketListId] = connection;

            connection.onicecandidate = (event) => {
              if (event.candidate) {
                if (!connection.remoteDescription) {
                  connection.pendingCandidates.push(event.candidate);
                } else {
                  socketRef.current.emit(
                    "signal",
                    socketListId,
                    JSON.stringify({ ice: event.candidate })
                  );
                }
              }
            };

            connection.ontrack = (event) => {
              const stream = event.streams[0];
              console.log(`Received stream for ${socketListId}`);
              updateVideos(socketListId, stream);
              if (videoRefs.current[socketListId]) {
                videoRefs.current[socketListId].srcObject = stream;
                videoRefs.current[socketListId]
                  .play()
                  .catch((e) =>
                    console.error(`Play error for ${socketListId}:`, e)
                  );
              }
            };

            if (localStreamRef.current) {
              localStreamRef.current.getTracks().forEach((track) => {
                connection.addTrack(track, localStreamRef.current);
              });
            }

            // Only create offer if connection is new and stable
            if (
              connection.signalingState === "stable" &&
              !connection.makingOffer
            ) {
              connection.makingOffer = true;
              connection
                .createOffer()
                .then((description) =>
                  connection.setLocalDescription(description)
                )
                .then(() => {
                  console.log(`Sending offer to ${socketListId}`);
                  socketRef.current.emit(
                    "signal",
                    socketListId,
                    JSON.stringify({ sdp: connection.localDescription })
                  );
                })
                .catch((e) => console.error("Error creating offer:", e))
                .finally(() => {
                  connection.makingOffer = false;
                  if (connection.pendingCandidates.length) {
                    connection.pendingCandidates.forEach((candidate) => {
                      connection
                        .addIceCandidate(candidate)
                        .catch((e) =>
                          console.error("Error adding queued ICE candidate:", e)
                        );
                    });
                    connection.pendingCandidates = [];
                  }
                });
            }
          }
        });
      });
      socketRef.current.on("transcription", (sender, transcript) => {
        if (sender !== userInfo?.u_name) {
          setTranscriptionMessages((prev) => [
            ...prev,
            { sender, data: transcript },
          ]);
        }
      });
    });
  };

  const gotMessageFromServer = async (fromId, message) => {
    const signal = JSON.parse(message);
    if (fromId === socketIdRef.current) return;

    let connection = connectionsRef.current[fromId];
    if (!connection) {
      connection = new RTCPeerConnection(peerConfigConnections);
      connection.makingOffer = false;
      connection.ignoreOffer = false;
      connection.pendingCandidates = [];
      connectionsRef.current[fromId] = connection;

      connection.onicecandidate = (event) => {
        if (event.candidate) {
          if (!connection.remoteDescription) {
            connection.pendingCandidates.push(event.candidate);
          } else {
            socketRef.current.emit(
              "signal",
              fromId,
              JSON.stringify({ ice: event.candidate })
            );
          }
        }
      };

      connection.ontrack = (event) => {
        const stream = event.streams[0];
        console.log(`Received stream from ${fromId}`);
        updateVideos(fromId, stream);
        if (videoRefs.current[fromId]) {
          videoRefs.current[fromId].srcObject = stream;
          videoRefs.current[fromId]
            .play()
            .catch((e) => console.error(`Play error for ${fromId}:`, e));
        }
      };

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          connection.addTrack(track, localStreamRef.current);
        });
      }
    }

    if (signal.sdp) {
      const currentState = connection.signalingState;
      console.log(
        `Processing SDP from ${fromId}: Type=${signal.sdp.type}, State=${currentState}`
      );

      const offerCollision =
        signal.sdp.type === "offer" &&
        (connection.makingOffer || currentState !== "stable");
      connection.ignoreOffer = !polite && offerCollision;

      if (connection.ignoreOffer) {
        console.warn(`Ignoring offer from ${fromId} due to collision`);
        return;
      }

      try {
        if (signal.sdp.type === "offer" && currentState === "stable") {
          console.log(`Setting remote offer from ${fromId}`);
          await connection.setRemoteDescription(
            new RTCSessionDescription(signal.sdp)
          );
          const answer = await connection.createAnswer();
          await connection.setLocalDescription(answer);
          console.log(`Sending answer to ${fromId}`);
          socketRef.current.emit(
            "signal",
            fromId,
            JSON.stringify({ sdp: connection.localDescription })
          );
        } else if (
          signal.sdp.type === "answer" &&
          currentState === "have-local-offer"
        ) {
          console.log(`Setting remote answer from ${fromId}`);
          await connection.setRemoteDescription(
            new RTCSessionDescription(signal.sdp)
          );
        } else {
          console.warn(
            `Skipping SDP from ${fromId}: Type=${signal.sdp.type}, State=${currentState} not applicable`
          );
          return;
        }

        if (connection.pendingCandidates.length) {
          for (const candidate of connection.pendingCandidates) {
            await connection
              .addIceCandidate(candidate)
              .catch((e) =>
                console.error("Error adding queued ICE candidate:", e)
              );
          }
          connection.pendingCandidates = [];
        }
      } catch (e) {
        console.error(`Error processing SDP from ${fromId}:`, e);
        if (e.name === "InvalidStateError" && currentState === "stable") {
          console.warn(
            `InvalidStateError detected, attempting renegotiation with ${fromId}`
          );
          renegotiateConnection(fromId, connection);
        }
      }
    }

    if (signal.ice) {
      try {
        if (connection.remoteDescription) {
          await connection.addIceCandidate(new RTCIceCandidate(signal.ice));
        } else {
          connection.pendingCandidates.push(new RTCIceCandidate(signal.ice));
        }
      } catch (e) {
        console.error("Error adding ICE candidate:", e);
      }
    }
  };

  const silence = () => {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const dst = oscillator.connect(ctx.createMediaStreamDestination());
    oscillator.start();
    ctx.resume();
    return Object.assign(dst.stream.getAudioTracks()[0], { enabled: false });
  };

  const black = ({ width = 640, height = 480 } = {}) => {
    const canvas = Object.assign(document.createElement("canvas"), {
      width,
      height,
    });
    canvas.getContext("2d").fillRect(0, 0, width, height);
    const stream = canvas.captureStream();
    return Object.assign(stream.getVideoTracks()[0], { enabled: false });
  };

  const handleVideo = () => setVideo(!video);
  const handleAudio = () => setAudio(!audio);
  const handleScreen = () => setScreen(!screen);
  const handleEndCall = () => {
    try {
      if (localStreamRef.current)
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      if (localVideoref.current && localVideoref.current.srcObject) {
        localVideoref.current.srcObject
          .getTracks()
          .forEach((track) => track.stop());
      }
    } catch (e) {
      console.log("Error stopping tracks on end call:", e);
    }
    navigate("/");
  };
  const handleRecord = () => {
    setIsRecording(!isRecording);
    console.log(isRecording ? "Recording stopped..." : "Recording started...");
  };
  const openChatDrawer = () => {
    setShowChatDrawer(true);
    setNewMessages(0);
  };
  const closeChatDrawer = () => setShowChatDrawer(false);
  const openAIDrawer = () => setShowAIDrawer(true);
  const closeAIDrawer = () => setShowAIDrawer(false);
  const handleMessage = (e) => setMessage(e.target.value);

  const sendMessage = () => {
    socketRef.current.emit("chat-message", userInfo.u_name, message);
    setMessages((prev) => [
      ...prev,
      { sender: userInfo.u_name, data: message },
    ]);
    setMessage("");
  };

  const sendAIQuery = () => {
    const queryData = { query: aiQuery };
    socketRef.current.emit("ai-query", queryData);
    setAiChatMessages((prev) => [
      ...prev,
      { sender: userInfo.u_name, data: aiQuery },
    ]);
    setAiQuery("");
  };

  const connect = async () => {
    if (!userInfo?.u_name) {
      console.error("User name not found in localStorage");
      return;
    }
    await getPermissions();
    await getUserMedia();
    setAskForUsername(false);
    setVideo(cameraOn && videoAvailable);
    setAudio(micOn && audioAvailable);
  };

  useEffect(() => {
    if (screen && !askForUsername) getDisplayMedia();
  }, [screen, askForUsername]);

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    return parts.length === 1
      ? name.charAt(0).toUpperCase()
      : parts
          .slice(0, 2)
          .map((part) => part.charAt(0).toUpperCase())
          .join("");
  };

  const hasActiveVideoTrack = (stream) => {
    if (!stream) return false;
    const videoTracks = stream.getVideoTracks();
    return videoTracks.length > 0 && videoTracks.some((track) => track.enabled);
  };

  const hasActiveAudioTrack = (stream) => {
    if (!stream) return false;
    const audioTracks = stream.getAudioTracks();
    return audioTracks.length > 0 && audioTracks.some((track) => track.enabled);
  };

  const getAvatarUrl = (seed) => {
    return `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(
      seed
    )}`;
  };

  const handleAudioDeviceSelection = async (event) => {
    const newAudioDeviceId = event.target.value;
    setAudioDevice(newAudioDeviceId);
    if (newAudioDeviceId === "none") {
      setAudio(false);
      if (localStreamRef.current)
        localStreamRef.current
          .getAudioTracks()
          .forEach((track) => track.stop());
      return;
    }
    try {
      if (localStreamRef.current)
        localStreamRef.current
          .getAudioTracks()
          .forEach((track) => track.stop());
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: newAudioDeviceId } },
        video:
          videoDevice && videoDevice !== "none"
            ? { deviceId: { exact: videoDevice } }
            : video && videoAvailable,
      });
      if (localStreamRef.current) {
        const videoTracks = localStreamRef.current.getVideoTracks();
        videoTracks.forEach((track) => audioStream.addTrack(track));
        localStreamRef.current = audioStream;
      } else {
        localStreamRef.current = audioStream;
      }
      if (localVideoref.current) {
        localVideoref.current.srcObject = localStreamRef.current;
        await localVideoref.current.play();
      }
      Object.keys(connectionsRef.current).forEach((id) => {
        if (id !== socketIdRef.current) {
          connectionsRef.current[id].addTrack(
            audioStream.getAudioTracks()[0],
            new MediaStream([audioStream.getAudioTracks()[0]])
          );
          if (
            connectionsRef.current[id].signalingState === "stable" &&
            !connectionsRef.current[id].makingOffer
          ) {
            connectionsRef.current[id].createOffer().then((description) => {
              connectionsRef.current[id]
                .setLocalDescription(description)
                .then(() => {
                  socketRef.current.emit(
                    "signal",
                    id,
                    JSON.stringify({
                      sdp: connectionsRef.current[id].localDescription,
                    })
                  );
                });
            });
          }
        }
      });
      setAudioAvailable(true);
      setCameraError(null);
    } catch (error) {
      console.error("Error changing audio device:", error);
      setAudioAvailable(false);
      setCameraError("Failed to switch audio device");
    }
  };

  const handleVideoDeviceSelection = async (event) => {
    const newVideoDeviceId = event.target.value;
    setVideoDevice(newVideoDeviceId);
    if (newVideoDeviceId === "none") {
      setVideo(false);
      if (localStreamRef.current)
        localStreamRef.current
          .getVideoTracks()
          .forEach((track) => track.stop());
      return;
    }
    try {
      if (localStreamRef.current)
        localStreamRef.current
          .getVideoTracks()
          .forEach((track) => track.stop());
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: newVideoDeviceId } },
        audio:
          audioDevice && audioDevice !== "none"
            ? { deviceId: { exact: audioDevice } }
            : audio && audioAvailable,
      });
      if (localStreamRef.current) {
        const audioTracks = localStreamRef.current.getAudioTracks();
        audioTracks.forEach((track) => videoStream.addTrack(track));
        localStreamRef.current = videoStream;
      } else {
        localStreamRef.current = videoStream;
      }
      if (localVideoref.current) {
        localVideoref.current.srcObject = localStreamRef.current;
        await localVideoref.current.play();
      }
      Object.keys(connectionsRef.current).forEach((id) => {
        if (id !== socketIdRef.current) {
          connectionsRef.current[id].addTrack(
            videoStream.getVideoTracks()[0],
            new MediaStream([videoStream.getVideoTracks()[0]])
          );
          if (
            connectionsRef.current[id].signalingState === "stable" &&
            !connectionsRef.current[id].makingOffer
          ) {
            connectionsRef.current[id].createOffer().then((description) => {
              connectionsRef.current[id]
                .setLocalDescription(description)
                .then(() => {
                  socketRef.current.emit(
                    "signal",
                    id,
                    JSON.stringify({
                      sdp: connectionsRef.current[id].localDescription,
                    })
                  );
                });
            });
          }
        }
      });
      setVideoAvailable(true);
      setCameraError(null);
    } catch (error) {
      console.error("Error changing video device:", error);
      setVideoAvailable(false);
      setCameraError("Failed to switch video device");
    }
  };

  const getPermissions = async () => {
    try {
      const videoNeeded = videoDevice && videoDevice !== "none";
      const audioNeeded = audioDevice && audioDevice !== "none";
      if (!videoNeeded && !audioNeeded) return;
      const videoConstraints = videoNeeded
        ? { video: { deviceId: { exact: videoDevice } } }
        : { video: false };
      const videoPermission = await navigator.mediaDevices.getUserMedia(
        videoConstraints
      );
      setVideoAvailable(!!videoPermission);
      videoPermission.getTracks().forEach((track) => track.stop());
      const audioConstraints = audioNeeded
        ? { audio: { deviceId: { exact: audioDevice } } }
        : { audio: false };
      const audioPermission = await navigator.mediaDevices.getUserMedia(
        audioConstraints
      );
      setAudioAvailable(!!audioPermission);
      audioPermission.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Error getting permissions:", error);
      setAudioAvailable(false);
      setVideoAvailable(false);
      setCameraError("Permission denied for audio/video");
    }
  };

  const getUserMedia = async () => {
    const videoNeeded = video && videoAvailable && videoDevice !== "none";
    const audioNeeded = audio && audioAvailable && audioDevice !== "none";
    if (!videoNeeded && !audioNeeded) return;
    const constraints = {
      video: videoNeeded ? { deviceId: { exact: videoDevice } } : false,
      audio: audioNeeded ? { deviceId: { exact: audioDevice } } : false,
    };
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      getUserMediaSuccess(stream);
    } catch (e) {
      console.log("Error in getUserMedia:", e);
      setCameraError("Failed to access selected device");
    }
  };

  const getDisplayMedia = () => {
    if (screen && navigator.mediaDevices.getDisplayMedia) {
      navigator.mediaDevices
        .getDisplayMedia({ video: true, audio: true })
        .then(getDisplayMediaSuccess)
        .catch((e) => console.log("Error in getDisplayMedia:", e));
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: "93vh",
          bgcolor: "#121212",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {askForUsername ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              height: "100%",
              bgcolor: "#121212",
            }}
          >
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ p: 3, bgcolor: "#1e1e1e" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Video Call
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Join the Video Call with Baali AI as{" "}
                  {userInfo?.u_name || "Guest"}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  bgcolor: "transparent",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: "400px" },
                    height: { xs: "200px", sm: "300px" },
                    borderRadius: "16px",
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.5)",
                    background: cameraOn
                      ? "transparent"
                      : "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <video
                    ref={previewVideoref}
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: cameraOn && previewStream ? "block" : "none",
                    }}
                  />
                  {!cameraOn && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        textAlign: "center",
                        px: 2,
                      }}
                    >
                      Your camera is turned off
                    </Typography>
                  )}
                </Box>
                {cameraError && (
                  <Typography variant="body2" sx={{ color: "#ff4d4d", mt: 2 }}>
                    {cameraError}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: "#1e1e1e",
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Switch
                  checked={cameraOn}
                  onChange={() => setCameraOn(!cameraOn)}
                  icon={<VideocamOffIcon sx={{ color: "#fff" }} />}
                  checkedIcon={<VideocamIcon sx={{ color: "#fff" }} />}
                />
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                  Camera
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", sm: "320px" },
                bgcolor: "rgba(255,255,255,0.03)",
                p: 3,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Media Settings
              </Typography>
              <FormControl variant="outlined" sx={{ mb: 3 }}>
                <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Video Device
                </InputLabel>
                <Select
                  value={videoDevice}
                  onChange={handleVideoDeviceSelection}
                  label="Video Device"
                >
                  <MenuItem value="none">Don't use video</MenuItem>
                  {videoDevices.map((device) => (
                    <MenuItem key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${device.deviceId}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" sx={{ mb: 3 }}>
                <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Audio Device
                </InputLabel>
                <Select
                  value={audioDevice}
                  onChange={handleAudioDeviceSelection}
                  label="Audio Device"
                >
                  <MenuItem value="none">Don't use audio</MenuItem>
                  {audioDevices.map((device) => (
                    <MenuItem key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {audioDevice !== "none" && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Switch
                      checked={micOn}
                      onChange={() => setMicOn(!micOn)}
                      icon={<MicOffIcon sx={{ color: "#fff" }} />}
                      checkedIcon={<MicIcon sx={{ color: "#fff" }} />}
                    />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                      Microphone
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Volume
                    </Typography>
                    <Slider
                      value={volume}
                      onChange={(e, newValue) => setVolume(newValue)}
                      min={0}
                      max={100}
                    />
                  </Box>
                </>
              )}
              <Box
                sx={{
                  mt: "auto",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button variant="outlined" onClick={() => navigate("/")}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={connect}
                  disabled={!userInfo?.u_name}
                >
                  Join now
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
            <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      position: "relative",
                      bgcolor: "#2e2e2e",
                      borderRadius: 2,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      border:
                        activeSpeaker === "local"
                          ? "3px solid #00ff00"
                          : "none",
                      animation:
                        activeSpeaker === "local"
                          ? "glow 1s ease-in-out infinite alternate"
                          : "none",
                      "@keyframes glow": {
                        "0%": { boxShadow: "0 0 5px #00ff00" },
                        "100%": { boxShadow: "0 0 20px #00ff00" },
                      },
                    }}
                  >
                    {hasActiveVideoTrack(localStreamRef.current) && video ? (
                      <video
                        ref={localVideoref}
                        autoPlay
                        muted
                        playsInline
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Avatar
                        src={getAvatarUrl(userInfo?.u_name || "default")}
                        sx={{
                          width: 100,
                          height: 100,
                          bgcolor: "#555",
                          fontSize: 40,
                        }}
                      >
                        {getInitials(userInfo?.u_name)}
                      </Avatar>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {userInfo?.u_name || "You"}
                    </Typography>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        bgcolor: "rgba(0,0,0,0.6)",
                        borderRadius: "50%",
                        p: 0.5,
                      }}
                    >
                      {audio && hasActiveAudioTrack(localStreamRef.current) ? (
                        <MicIcon sx={{ color: "#fff", fontSize: 20 }} />
                      ) : (
                        <MicOffIcon sx={{ color: "#ff0000", fontSize: 20 }} />
                      )}
                    </Box>
                  </Box>
                </Grid>
                {allParticipants.map((participant) => (
                  <Grid item xs={12} sm={6} md={4} key={participant.socketId}>
                    <Box
                      sx={{
                        position: "relative",
                        bgcolor: "#2e2e2e",
                        borderRadius: 2,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        border:
                          activeSpeaker === participant.socketId
                            ? "3px solid #00ff00"
                            : "none",
                        animation:
                          activeSpeaker === participant.socketId
                            ? "glow 1s ease-in-out infinite alternate"
                            : "none",
                        "@keyframes glow": {
                          "0%": { boxShadow: "0 0 5px #00ff00" },
                          "100%": { boxShadow: "0 0 20px #00ff00" },
                        },
                      }}
                    >
                      {participant.socketId === "baali-ai-agent" ? (
                        <Avatar
                          src={getAvatarUrl(
                            participant.name || participant.socketId
                          )}
                          sx={{
                            width: 100,
                            height: 100,
                            bgcolor: "#555",
                            fontSize: 40,
                          }}
                        >
                          {getInitials(
                            participant.name || participant.socketId
                          )}
                        </Avatar>
                      ) : !participant.stream ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(0,0,0,0.4)",
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            Waiting...
                          </Typography>
                        </Box>
                      ) : hasActiveVideoTrack(participant.stream) ? (
                        <ParticipantVideo
                          participant={participant}
                          ref={(el) =>
                            (videoRefs.current[participant.socketId] = el)
                          }
                        />
                      ) : (
                        <Avatar
                          src={getAvatarUrl(
                            participant.name || participant.socketId
                          )}
                          sx={{
                            width: 100,
                            height: 100,
                            bgcolor: "#555",
                            fontSize: 40,
                          }}
                        >
                          {getInitials(
                            participant.name || participant.socketId
                          )}
                        </Avatar>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {participant.name || participant.socketId}
                      </Typography>
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          left: 8,
                          bgcolor: "rgba(0,0,0,0.6)",
                          borderRadius: "50%",
                          p: 0.5,
                        }}
                      >
                        {hasActiveAudioTrack(participant.stream) ? (
                          <MicIcon sx={{ color: "#fff", fontSize: 20 }} />
                        ) : (
                          <MicOffIcon sx={{ color: "#ff0000", fontSize: 20 }} />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1,
                bgcolor: "#1c2526",
                borderRadius: "8px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}></Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <IconButton
                  onClick={handleAudio}
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e0e0e0" } }}
                >
                  {audio ? (
                    <MicIcon sx={{ color: "black" }} />
                  ) : (
                    <MicOffIcon sx={{ color: "black" }} />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleVideo}
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e0e0e0" } }}
                >
                  {video ? (
                    <VideocamIcon sx={{ color: "black" }} />
                  ) : (
                    <VideocamOffIcon sx={{ color: "black" }} />
                  )}
                </IconButton>
                {screenAvailable && (
                  <IconButton
                    onClick={handleScreen}
                    sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e0e0e0" } }}
                  >
                    {screen ? (
                      <ScreenShareIcon sx={{ color: "black" }} />
                    ) : (
                      <StopScreenShareIcon sx={{ color: "black" }} />
                    )}
                  </IconButton>
                )}
                <IconButton
                  onClick={handleRecord}
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e0e0e0" } }}
                >
                  {isRecording ? (
                    <StopIcon sx={{ color: "black" }} />
                  ) : (
                    <FiberManualRecordIcon sx={{ color: "black" }} />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleEndCall}
                  sx={{ bgcolor: "red", "&:hover": { bgcolor: "#d32f2f" } }}
                >
                  <CallEndIcon sx={{ color: "white" }} />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <IconButton
                  onClick={openChatDrawer}
                  sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e0e0e0" } }}
                >
                  <Badge badgeContent={newMessages} color="error">
                    <ChatIcon sx={{ color: "black" }} />
                  </Badge>
                </IconButton>
                {userInfo?.u_role !== "candidate" && (
                  <IconButton
                    onClick={openAIDrawer}
                    sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e0e0e0" } }}
                  >
                    <SmartToyIcon sx={{ color: "black" }} />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Drawer
              anchor="right"
              open={showChatDrawer}
              onClose={closeChatDrawer}
              sx={{
                "& .MuiDrawer-paper": {
                  width: 350,
                  bgcolor: "#1e1e1e",
                  color: "#fff",
                  p: 2,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Chat</Typography>
                <IconButton onClick={closeChatDrawer} sx={{ color: "#fff" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
                {messages.length > 0 ? (
                  messages.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold" }}
                      >
                        {item.sender}
                      </Typography>
                      <Typography variant="body2">{item.data}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography>No Messages Yet</Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  value={message}
                  onChange={handleMessage}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && message.trim() !== "")
                      sendMessage();
                  }}
                  label="Type a message"
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1,
                    bgcolor: "#1e1e1e",
                    borderRadius: 1,
                    "& .MuiInputBase-root": { color: "#fff" },
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={sendMessage}
                >
                  Send
                </Button>
              </Box>
            </Drawer>
            {userInfo?.u_role !== "candidate" && (
              <Drawer
                anchor="right"
                open={showAIDrawer}
                onClose={closeAIDrawer}
                sx={{
                  "& .MuiDrawer-paper": {
                    width: 350,
                    bgcolor: "#1e1e1e",
                    color: "#fff",
                    p: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">AI Chat</Typography>
                  <IconButton onClick={closeAIDrawer} sx={{ color: "#fff" }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
                  {aiChatMessages.length > 0 ? (
                    aiChatMessages.map((msg, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {msg.sender}
                        </Typography>
                        <Typography variant="body2">{msg.data}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography>No AI chat messages yet.</Typography>
                  )}
                </Box>
                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  <TextField
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    label="Ask AI"
                    variant="outlined"
                    size="small"
                    sx={{
                      bgcolor: "#1e1e1e",
                      "& .MuiInputBase-root": { color: "#fff" },
                      "& .MuiInputLabel-root": { color: "#fff" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.3)",
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && aiQuery.trim() !== "")
                        sendAIQuery();
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={sendAIQuery}
                    sx={{ mt: 1 }}
                  >
                    Send Query
                  </Button>
                </Box>
              </Drawer>
            )}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => ({
  dashboard: state.dashboard,
  currentUserDetails: state.auth.user,
});

const mapDispatchToProps = {
  Userprofile,
  hideSidebar,
  showSidebar,
  hideHeader,
  showHeader,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(VideoCall));
